import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { useIntl } from 'react-intl';

import { getLinkAriaLabel } from '@/utils/urlUtils';

import { FreeTextContent } from '@/components/Cms/FreeTextContent/index';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { LegacyButton } from '@/components/LegacyButton';
import type { ContentfulPlaceholderFragment } from '@/graphql/contentful/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { EVENT_NAME } from '@/lib/gtm/const';
import { useRegionProvider } from '@/providers/RegionProvider';

import { ReactComponent as WarrantyDurationSVG } from '@/assets/warranty-duration.svg';

import * as M from './messages';
import * as S from './WarrantyDetails.styles';

export interface WarrantyData {
  movementManufacture: string;
  subtext?: ContentfulPlaceholderFragment['subtext'];
  warrantyDuration: string;
  warrantyExtDuration: string;
  warrantyText?: ContentfulPlaceholderFragment['warrantyText'];
}

export const WarrantyDetails = ({
  movementManufacture,
  subtext,
  warrantyDuration,
  warrantyExtDuration,
  warrantyText,
}: WarrantyData) => {
  const { formatMessage } = useIntl();
  const { sendEvent } = useGoogleAnalytics();
  const { locale } = useRegionProvider();
  const containsYes = (text: string) => {
    return text.toLowerCase()?.includes('yes');
  };
  const showManufacture = containsYes(movementManufacture);
  return (
    <S.Wrapper>
      <S.DescriptionWrapper>
        {subtext?.json ? (
          <S.Description
            dangerouslySetInnerHTML={{
              __html: documentToHtmlString(subtext?.json),
            }}
            data-testid='warrantyDetailsDescription'
          />
        ) : (
          <S.Description data-testid='warrantyDetailsDescription'>
            {M.Description}
          </S.Description>
        )}
        <S.WarrantyWrapper>
          <S.WarrantyDuration data-testid='warrantyDetailsDurationInSvg'>
            {warrantyDuration}
          </S.WarrantyDuration>
          <WarrantyDurationSVG />
        </S.WarrantyWrapper>
      </S.DescriptionWrapper>
      <S.FlexSpace />
      <S.Duration data-testid='warrantyDetailsDuration'>
        {warrantyText?.placeholderWarrantyHeading?.text ?? M.Duration}
        {': '}
        <S.DurationValue as='span' data-testid='warrantyDetailsDurationInText'>
          {warrantyDuration}
        </S.DurationValue>
      </S.Duration>
      <S.Terms>
        {warrantyText?.placeholderWarrantyText?.json ? (
          <FreeTextContent
            freeTextContent={{
              __typename: 'FreeTextContentTextContent',
              json: warrantyText?.placeholderWarrantyText?.json,
              links: {
                assets: {
                  block: [],
                },
              },
            }}
            sys={{ __typename: 'Sys', id: 'placeholderWarrantyText' }}
          />
        ) : (
          <S.TermsText>{M.WarrantyTerms()}</S.TermsText>
        )}
      </S.Terms>

      <S.ExtendWrapper>
        <Flex>
          {showManufacture ? (
            <S.ExtendDetails>
              <S.ExtendDetailsYears data-testid='warrantyDetailsExtMechanicalVal'>
                {formatMessage(M.defined.years, { warrantyExtDuration })}
              </S.ExtendDetailsYears>
              <S.ExtendDetailsInfo data-testid='warrantyDetailsExtMechanicalText'>
                {M.MechanicalMovements}
              </S.ExtendDetailsInfo>
            </S.ExtendDetails>
          ) : (
            <S.ExtendDetails>
              <S.ExtendDetailsYears data-testid='warrantyDetailsExtOtherVal'>
                {formatMessage(M.defined.years, { warrantyExtDuration })}
              </S.ExtendDetailsYears>
              <S.ExtendDetailsInfo data-testid='warrantyDetailsExtOtherText'>
                {M.OtherMovements}
              </S.ExtendDetailsInfo>
            </S.ExtendDetails>
          )}
        </Flex>
        {warrantyText?.placeholderWarrantyText2 ? (
          <S.DescriptionText
            dangerouslySetInnerHTML={{
              __html: documentToHtmlString(
                warrantyText.placeholderWarrantyText2.json,
              ),
            }}
            data-testid='warrantyDetailsExtLink'
            marginBottom={2}
          />
        ) : (
          <S.DescriptionText marginBottom={2}>
            {M.ExtendDescription}
          </S.DescriptionText>
        )}

        <LegacyButton
          aria-label={getLinkAriaLabel(warrantyText?.url ?? '/', locale.code)}
          data-testid='warrantyDetailsExtLink'
          href={warrantyText?.url ?? '/'}
          label={warrantyText?.urlText ?? M.ExtendLink}
          onClick={() =>
            sendEvent({ event: EVENT_NAME.INTENTION_WARRANTY_EXTENSION })
          }
          size='l'
          variant='link'
          width='auto'
        >
          <Icon name='chevronRight' />
        </LegacyButton>
      </S.ExtendWrapper>
    </S.Wrapper>
  );
};
